import { useCallback, useState } from 'react'

import { calculateCartTotal } from './cartHelpers'
import { CanteenCartItem, CanteenCartItemInput } from './types'

const getCartItemReference = (cartItem: CanteenCartItemInput) => {
  // Current ref template is: I(itemId)-S(submenuId)-O[optionId1, optionId2]
  const { itemId, options, submenuId } = cartItem
  const optionIds =
    options != null && options.length > 0 ? options.map((option) => option.id).join('-') : ''
  const id = `I(${itemId})-S(${submenuId})${
    optionIds !== '' ? `-O(${options.map((option) => option.id).join('-')})` : ''
  }`
  return id
}

export const useCartState = () => {
  const [cartItems, setCartItems] = useState<Record<string, CanteenCartItem>>({})

  const updateCartItem = (updatedItem: CanteenCartItem) => {
    setCartItems((prev) => ({
      ...prev,
      [updatedItem.reference]: updatedItem,
    }))
  }

  const addCartItem = (cartItem: CanteenCartItemInput, arrivalTime: string) => {
    const itemReference = getCartItemReference(cartItem)
    let newQuantity = cartItem.qty

    if (cartItem.orderLimit && cartItem.quantityRemaining) {
      newQuantity = Math.min(
        cartItem.orderLimit,
        cartItem.quantityRemaining,
        (cartItems[itemReference]?.qty ?? 0) + cartItem.qty,
      )
    }

    const itemToAdd: CanteenCartItem = {
      ...cartItem,
      reference: getCartItemReference(cartItem),
      arrivalTime,
      qty: newQuantity,
    }
    setCartItems((prev) => ({
      ...prev,
      [itemReference]: itemToAdd,
    }))
  }

  const removeCartItem = (itemReference: string) => {
    setCartItems((prev) => {
      const newItems = { ...prev }
      delete newItems[itemReference]
      return newItems
    })
  }

  const emptyCart = useCallback(() => {
    setCartItems({})
  }, [])

  const cartItemsArray = Object.values(cartItems)
  const cartTotal = calculateCartTotal(cartItemsArray)
  const itemQuantity = cartItemsArray.reduce((rtn, item) => rtn + item.qty, 0)

  return {
    addCartItem,
    removeCartItem,
    updateCartItem,
    emptyCart,
    cartItems: cartItemsArray,
    cartTotal,
    itemQuantity,
  }
}
